import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Menu, X } from "lucide-react";
import useLogout from "components/common/useLogout";
import { useAdminPermissions } from "../../../hooks/useAdminPermissions";
import Logo from "../../../assets/images/logo/logo.png";
import "./AdminNavbar.css";

interface NavLink {
  pathname: string;
  name: string;
}

// Ordered by priority. As space allows, these fill the navbar left-to-right.
// Anything that doesn't fit overflows into the drawer (no duplication).
const PRIMARY_LINKS: NavLink[] = [
  { pathname: "/admin/approvals", name: "User Management" },
  { pathname: "/inbox/gmail", name: "Inbox" },
  { pathname: "/admin/help-center", name: "Help Center" },
  { pathname: "/admin/products", name: "Products" },
  { pathname: "/admin/release-config", name: "Release Config" },
];

// Always lives in the drawer.
const SECONDARY_LINKS: NavLink[] = [
  { pathname: "/admin/permissions", name: "Permissions" },
  { pathname: "/admin/announcements", name: "Announcements" },
  { pathname: "/admin/upgrade-banner", name: "Upgrade Banner" },
  { pathname: "/admin/invite", name: "Invite" },
  { pathname: "/admin/pricing", name: "Pricing" },
  { pathname: "/admin/supademos", name: "Supademos" },
];

// Links that require super admin status.
const SUPER_ADMIN_ONLY_PATHS = new Set<string>(["/admin/permissions"]);

// Maps a route pathname to the permission key required to access it.
// Paths not listed here are visible to all admins.
const PERMISSION_REQUIRED: Record<string, string> = {
  "/admin/approvals": "approve_users",
  "/inbox/gmail": "check_inbox",
  "/admin/products": "products",
  "/admin/release-config": "release_configs",
  "/admin/announcements": "announcements",
  "/admin/upgrade-banner": "manage_upgrade_banner",
  "/admin/invite": "invites",
  "/admin/pricing": "adjust_pricing",
  "/admin/supademos": "supademos",
};

const LINK_GAP = 4; // Must match CSS gap on .admin-navbar__links
const MOBILE_BREAKPOINT = 640; // Below this, all primary links move to drawer

const AdminNavbar: React.FC = () => {
  const handleLogout = useLogout();
  const { pathname } = useLocation();
  const history = useHistory();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState<number>(
    PRIMARY_LINKS.length,
  );

  const linksAreaRef = useRef<HTMLDivElement | null>(null);
  const measureItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const linkWidthsRef = useRef<number[]>([]);

  // Close drawer on route change
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDrawerOpen]);

  const measureLinkWidths = useCallback(() => {
    const widths = measureItemRefs.current.map(
      (el) => el?.getBoundingClientRect().width ?? 0,
    );
    linkWidthsRef.current = widths;
  }, []);

  const computeVisibleCount = useCallback(() => {
    const container = linksAreaRef.current;
    if (!container) return;

    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      setVisibleCount(0);
      return;
    }

    const widths = linkWidthsRef.current;
    if (!widths.length) {
      setVisibleCount(PRIMARY_LINKS.length);
      return;
    }

    const available = container.getBoundingClientRect().width;
    let used = 0;
    let count = 0;
    for (let i = 0; i < widths.length; i++) {
      const next = used + widths[i] + (count > 0 ? LINK_GAP : 0);
      if (next <= available) {
        used = next;
        count += 1;
      } else {
        break;
      }
    }
    setVisibleCount(count);
  }, []);

  useLayoutEffect(() => {
    measureLinkWidths();
    computeVisibleCount();
  }, [measureLinkWidths, computeVisibleCount]);

  useEffect(() => {
    const handleResize = () => {
      measureLinkWidths();
      computeVisibleCount();
    };

    window.addEventListener("resize", handleResize);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && linksAreaRef.current) {
      observer = new ResizeObserver(() => computeVisibleCount());
      observer.observe(linksAreaRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      observer?.disconnect();
    };
  }, [measureLinkWidths, computeVisibleCount]);

  const {
    hasPermission,
    isSuperAdmin,
    isLoading: isPermLoading,
  } = useAdminPermissions();

  const visiblePrimary = useMemo(
    () =>
      PRIMARY_LINKS.slice(0, visibleCount).filter((link) => {
        const req = PERMISSION_REQUIRED[link.pathname];
        return !req || isPermLoading || hasPermission(req);
      }),
    [visibleCount, hasPermission, isPermLoading],
  );

  const overflowPrimary = useMemo(
    () =>
      PRIMARY_LINKS.slice(visibleCount).filter((link) => {
        const req = PERMISSION_REQUIRED[link.pathname];
        return !req || isPermLoading || hasPermission(req);
      }),
    [visibleCount, hasPermission, isPermLoading],
  );

  const visibleSecondary = useMemo(
    () =>
      SECONDARY_LINKS.filter((link) => {
        if (SUPER_ADMIN_ONLY_PATHS.has(link.pathname)) return isSuperAdmin;
        const req = PERMISSION_REQUIRED[link.pathname];
        return !req || isPermLoading || hasPermission(req);
      }),
    [isSuperAdmin, hasPermission, isPermLoading],
  );

  const handleNavigate = useCallback(
    (path: string) => {
      history.push(path);
      setIsDrawerOpen(false);
    },
    [history],
  );

  const renderInlineLink = (link: NavLink) => {
    const isActive = pathname === link.pathname;
    return (
      <button
        key={link.pathname}
        type="button"
        onClick={() => handleNavigate(link.pathname)}
        className={`admin-navbar__link ${
          isActive ? "admin-navbar__link--active" : ""
        }`}
      >
        <span className="admin-navbar__link-label">{link.name}</span>
        {isActive && (
          <motion.span
            layoutId="admin-navbar-active-pill"
            className="admin-navbar__link-pill"
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
          />
        )}
      </button>
    );
  };

  const renderDrawerLink = (link: NavLink, idx: number) => {
    const isActive = pathname === link.pathname;
    return (
      <motion.button
        key={link.pathname}
        type="button"
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: idx * 0.03, duration: 0.2 }}
        onClick={() => handleNavigate(link.pathname)}
        className={`admin-navbar__drawer-link ${
          isActive ? "admin-navbar__drawer-link--active" : ""
        }`}
      >
        {link.name}
      </motion.button>
    );
  };

  return (
    <nav className="admin-navbar">
      <div className="admin-navbar__container">
        <div
          className="admin-navbar__brand"
          onClick={() => handleNavigate("/")}
          role="button"
          tabIndex={0}
        >
          <img src={Logo} alt="My Rep" className="admin-navbar__logo" />
        </div>

        <div
          className="admin-navbar__links admin-navbar__links--desktop"
          ref={linksAreaRef}
        >
          {visiblePrimary.map(renderInlineLink)}
        </div>

        {/* Hidden measurement row - used to compute natural link widths */}
        <div className="admin-navbar__measure-row" aria-hidden="true">
          {PRIMARY_LINKS.map((link, idx) => (
            <button
              key={link.pathname}
              type="button"
              tabIndex={-1}
              ref={(el) => {
                measureItemRefs.current[idx] = el;
              }}
              className="admin-navbar__link"
            >
              <span className="admin-navbar__link-label">{link.name}</span>
            </button>
          ))}
        </div>

        <div className="admin-navbar__actions">
          <button
            type="button"
            onClick={handleLogout}
            className="admin-navbar__logout admin-navbar__logout--desktop"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>

          <button
            type="button"
            className="admin-navbar__menu-toggle"
            aria-label={isDrawerOpen ? "Close menu" : "Open menu"}
            aria-expanded={isDrawerOpen}
            onClick={() => setIsDrawerOpen((prev) => !prev)}
          >
            <Menu size={18} />
            <span className="admin-navbar__menu-toggle-label">Menu</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isDrawerOpen && (
          <>
            <motion.div
              className="admin-navbar__backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsDrawerOpen(false)}
            />
            <motion.aside
              className="admin-navbar__drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
              role="dialog"
              aria-label="Navigation menu"
            >
              <div className="admin-navbar__drawer-header">
                <span className="admin-navbar__drawer-title">Menu</span>
                <button
                  type="button"
                  className="admin-navbar__drawer-close"
                  aria-label="Close menu"
                  onClick={() => setIsDrawerOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="admin-navbar__drawer-body">
                {overflowPrimary.length > 0 && (
                  <div className="admin-navbar__drawer-section">
                    <span className="admin-navbar__drawer-section-title">
                      Navigation
                    </span>
                    <div className="admin-navbar__drawer-links">
                      {overflowPrimary.map((link, idx) =>
                        renderDrawerLink(link, idx),
                      )}
                    </div>
                  </div>
                )}

                <div className="admin-navbar__drawer-section">
                  <span className="admin-navbar__drawer-section-title">
                    More
                  </span>
                  <div className="admin-navbar__drawer-links">
                    {visibleSecondary.map((link, idx) =>
                      renderDrawerLink(link, idx + overflowPrimary.length),
                    )}
                  </div>
                </div>
              </div>

              <div className="admin-navbar__drawer-footer">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="admin-navbar__logout admin-navbar__logout--drawer"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default AdminNavbar;
